/**
 * @file canvas.ts
 * @description Какой адрес витрины показывает канва конструктора
 * (phase19.16 Р1-6а; uiux-builder-shell.md §2.1, контракт §14.1).
 *
 * Черновик: страница конструктора — `/preview/<slug>` (неопубликованная
 * тоже, с тулбарами блоков), экран магазина — его адрес, лента блога —
 * `/blog`. «Сейчас на сайте» — опубликованный адрес страницы (`/`, `/<slug>`).
 * Вход в канву — ключом вида сайта с этим путём (`/site-preview?…&path=`).
 *
 * Когда канву перерисовывать (`canvasReloadKey`): цвета, шрифты, формы,
 * размеры и отступы витрина принимает живым сообщением без перезагрузки —
 * их сохранение канву не трогает. Перерисовка нужна, только если изменилось
 * то, что витрина собирает на сервере: шапка, подвал, рисунки задника,
 * название и логотипы, меню, SEO, лента блога.
 *
 * phase19.16 Р2а: панель «Каталог» и места над/под списком — каталог
 * (`/catalog` или выбранная категория «Показать: …»); экран «Страница товара»
 * — товар для примера шаблона. Вид каталога (часть CATALOG) витрина собирает
 * на сервере — после сохранения канва перерисовывается.
 *
 * SB120 этап 4: раздел «Вид корзины» — адрес экрана «Корзина» с любой
 * страницы (`/?cart=open`); вид корзины — часть THEME, живым сообщением.
 */

import { SITE_SLOTS, type SiteViewPart } from "@chaiberi/shared";

import { PREVIEW_SCREENS, screenPath, type PreviewScreenKey, type PreviewWidth } from "../site-view/preview";
import type { SiteViewDraft } from "../site-view/types";
import type { BuilderSelection } from "./selection";

interface CanvasPage {
  slug: string;
  kind: string;
  slot?: string | null;
}

/** Адрес каталога на канве: весь каталог или категория «Показать: …». */
export function catalogPath(categorySlug: string | null): string {
  return categorySlug ? `/catalog/${categorySlug}` : "/catalog";
}

/** Адрес экрана «Корзина» (открытая корзина с образцом в предпросмотре, `?cart=open`). */
const CART_PATH = PREVIEW_SCREENS.find((s) => s.key === "cart")?.path ?? "/?cart=open";

/** Место секций каталога (над/под списком) — видно на каталоге, а не на блоге. */
function isCatalogSlot(page: CanvasPage): boolean {
  return SITE_SLOTS.some((s) => s.key === page.slot && s.screen === "catalog");
}

/**
 * Что показывает канва: черновик (страница — `/preview/<slug>`), сайт сейчас
 * или снимок — сохранённый дизайн или версия из истории. Ключ снимка
 * страницы конструктора не открывает, поэтому снимок, как и сайт сейчас,
 * идёт по опубликованному адресу (`/`, `/<slug>`).
 */
export type CanvasPathMode = "draft" | "live" | "snapshot";

export function canvasPath(
  selection: Pick<BuilderSelection, "screen" | "panel">,
  page: CanvasPage | null,
  mode: CanvasPathMode,
  firstProductSlug: string | null = null,
  categorySlug: string | null = null,
): string {
  if (selection.panel === "blog") return "/blog";
  if (selection.panel === "catalog" || selection.screen === "catalog") return catalogPath(categorySlug);
  // Вид корзины (SB120 этап 4) — с любой страницы канва показывает открытую корзину с образцом.
  if (selection.panel === "cart") return CART_PATH;
  // Черновик шаблона товара — канва витрины `/preview/product/<slug>` (тулбары
  // блоков и живой `block-draft`); сайт сейчас и снимок — страница товара.
  if (selection.screen === "product" && mode === "draft" && firstProductSlug) return `/preview/product/${firstProductSlug}`;
  if (selection.screen) {
    const screen = PREVIEW_SCREENS.find((s) => s.key === selection.screen);
    return screen ? screenPath(screen, firstProductSlug) : "/";
  }
  if (!page) return "/";
  // Место для секций (над/под лентой, под статьёй) видно на странице блога, места каталога — в каталоге.
  if (page.kind === "SLOT") return isCatalogSlot(page) ? catalogPath(categorySlug) : "/blog";
  if (mode !== "draft") return page.kind === "SYSTEM_HOME" ? "/" : `/${page.slug}`;
  return `/preview/${page.slug}`;
}

/**
 * Экран предпросмотра на весь экран («Весь сайт ↗») для того, что открыто на
 * канве: экран магазина, лента блога, главная или страница по адресу.
 */
export function previewFocusFor(
  selection: Pick<BuilderSelection, "screen" | "panel">,
  page: CanvasPage | null,
): { screen: PreviewScreenKey; target: string | null } {
  if (selection.panel === "blog") return { screen: "blog", target: null };
  if (selection.panel === "catalog") return { screen: "catalog", target: null };
  if (selection.panel === "cart") return { screen: "cart", target: null };
  if (selection.screen) return { screen: selection.screen, target: null };
  if (!page || page.kind === "SYSTEM_HOME") return { screen: "home", target: null };
  if (page.kind === "SLOT") return { screen: isCatalogSlot(page) ? "catalog" : "blog", target: null };
  return { screen: "page", target: page.slug };
}

/** Части черновика, которые витрина не умеет принять живым сообщением. */
const RELOAD_PARTS: readonly SiteViewPart[] = ["BRAND", "MENUS", "SEO", "BLOG", "CATALOG"];

/**
 * Ключ перерисовки канвы: меняется, только когда без перезагрузки новое не
 * показать. Правка цвета или шрифта (часть THEME) ключ не меняет — канва
 * перекрашивается сообщением `commora:site-theme-preview`.
 */
export function canvasReloadKey(draft: Pick<SiteViewDraft, "parts" | "theme"> | null): string {
  if (!draft) return "";
  const versions = RELOAD_PARTS.map((part) => draft.parts[part]?.version ?? 0).join(".");
  const { header, footer, backdrop } = draft.theme;
  return `${versions}|${JSON.stringify({ header, footer, backdrop })}`;
}

/**
 * Устройство канвы по умолчанию — по её ширине в окне: узкая колонка
 * (планшет 768, телефон) показывает сайт как на телефоне, средняя — как на
 * планшете; иначе сайт ПК в колонке 440 мельчит до нечитаемого.
 */
export function deviceForCanvas(columnPx: number): PreviewWidth {
  if (columnPx <= 560) return 360;
  if (columnPx <= 900) return 768;
  return 1280;
}
